/**
 * Collapsible sidebar for onboarding navigation
 * Shows setup steps vertically, user info, and restaurant name
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Building,
  CreditCard,
  Printer,
  Globe,
  Laptop,
  MapPin,
  Truck,
  LogOut,
  User,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import { useOnboarding } from '@/hooks/useOnboarding';
import { useAuth } from '@/context/auth';
import { OnboardingSection, OnboardingStatus } from '@/types/onboarding';

// Section configuration with icons
const ONBOARDING_SECTIONS = [
  {
    id: OnboardingSection.BUSINESS_DETAILS,
    title: 'Business Details',
    icon: Building,
  },
  {
    id: OnboardingSection.STRIPE_CONNECT,
    title: 'Payments',
    icon: CreditCard,
  },
  {
    id: OnboardingSection.DOMAIN,
    title: 'Domain',
    icon: Globe,
  },
  {
    id: OnboardingSection.WEBSITE,
    title: 'Website',
    icon: Laptop,
  },
  {
    id: OnboardingSection.GOOGLE_BUSINESS,
    title: 'Google',
    icon: MapPin,
  },
  {
    id: OnboardingSection.AGGREGATORS,
    title: 'Aggregators',
    icon: Truck,
  },
  {
    id: OnboardingSection.PRINTER,
    title: 'Printer',
    icon: Printer,
  },
];

// Status badge configuration
const STATUS_CONFIG = {
  [OnboardingStatus.NOT_STARTED]: {
    label: 'Not Started',
    variant: 'secondary' as const,
    color: 'text-gray-500',
  },
  [OnboardingStatus.AWAITING_CONFIRMATION]: {
    label: 'Review',
    variant: 'default' as const,
    color: 'text-blue-600',
  },
  [OnboardingStatus.NEEDS_ATTENTION]: {
    label: 'Attention',
    variant: 'destructive' as const,
    color: 'text-red-600',
  },
  [OnboardingStatus.COMPLETE]: {
    label: 'Complete',
    variant: 'default' as const,
    color: 'text-green-600',
  },
};

interface OnboardingSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const OnboardingSidebar: React.FC<OnboardingSidebarProps> = ({
  activeSection,
  onSectionChange,
}) => {
  const { state } = useSidebar();
  const { progress, getRestaurantName } = useOnboarding();
  const { user: authUser, logout, isAuthenticated } = useAuth();

  // Get restaurant name from onboarding data
  const restaurantName = getRestaurantName();

  // Get user initials
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get section status
  const getSectionStatus = (sectionId: OnboardingSection) => {
    const section = progress?.sections.find(s => s.section === sectionId);
    return section?.status || OnboardingStatus.NOT_STARTED;
  };

  // Check if section is active
  const isSectionActive = (sectionId: OnboardingSection) => {
    return activeSection === sectionId;
  };

  // Show loading state if user not authenticated
  if (!isAuthenticated || !authUser) {
    return null;
  }

  // Format role display
  const formatRole = (role: string) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Get effective role (JWT takes precedence)
  const effectiveRole = authUser.jwt_role || authUser.role;

  return (
    <Sidebar variant="inset" className="border-r">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue text-white">
            <Building className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={cn(
              "font-semibold text-sm transition-opacity duration-200",
              state === "collapsed" && "opacity-0"
            )}>
              {restaurantName}
            </div>
            <div className={cn(
              "text-xs text-muted-foreground transition-opacity duration-200",
              state === "collapsed" && "opacity-0"
            )}>
              Onboarding Setup
            </div>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Setup Progress</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {ONBOARDING_SECTIONS.map((section) => {
                const Icon = section.icon;
                const status = getSectionStatus(section.id);
                const statusConfig = STATUS_CONFIG[status];
                const isActive = isSectionActive(section.id);

                return (
                  <SidebarMenuItem key={section.id}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => onSectionChange(section.id)}
                      className="flex items-center justify-between"
                      tooltip={state === "collapsed" ? section.title : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={cn(
                          "h-4 w-4",
                          isActive ? "text-white" : statusConfig.color
                        )} />
                        <span className="font-medium">{section.title}</span>
                      </div>
                      {state === "expanded" && (
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={statusConfig.variant}
                            className="text-xs px-2 py-0.5"
                          >
                            {statusConfig.label}
                          </Badge>
                          {isActive && (
                            <ChevronRight className="h-3 w-3 text-white" />
                          )}
                        </div>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarSeparator className="mb-4" />
        
        {/* User Profile Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-brand-blue text-white text-sm font-medium">
                {getUserInitials(authUser.name)}
              </AvatarFallback>
            </Avatar>
            <div className={cn(
              "flex-1 min-w-0 transition-opacity duration-200",
              state === "collapsed" && "opacity-0"
            )}>
              <div className="font-medium text-sm truncate">{authUser.name}</div>
              <div className="text-xs text-muted-foreground truncate">{authUser.email}</div>
            </div>
          </div>

          {state === "expanded" && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {formatRole(effectiveRole)}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}

          {state === "collapsed" && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default OnboardingSidebar;